import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    clearParticipantJobReadiness,
    loadParticipantJobReadiness
} from '../../../../store/participantService';
import { selectUsersForNames } from '../../../../store/userSelectors';
import { loadUsersMinimalDetails } from '../../../../store/userService';
import { EDIT_ON_CLOSED_ROLES } from '../../../../utils/formValidation/commonConstants';
import { PARTICIPANT_JOB_READINESS_ERROR } from '../../../../utils/formValidation/loadingErrorMessageConstants';
import { stableSort } from '../../../../utils/sortFunctions';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../../utils/userRoles';
import Button from '../../../formElements/Button';
import ResultsTable from '../../../table/ResultsTable';
import LoadingSpinner from '../../../ui/LoadingSpinner';

import CreateEditJobReadiness from './CreateEditJobReadiness';
import JobReadinessTableRow from './JobReadinessTableRow';

import app from '../../../../app.module.css';

const _HEAD_CELLS = [
    { id: 'jobReadiness', numeric: false, label: 'Job Readiness' },
    { id: 'jobReadinessReason', numeric: false, label: 'Reason' },
    { id: 'createdDate', numeric: false, label: 'Date Created' },
    { id: 'user', numeric: false, label: 'User' },
    { id: 'edit', numeric: false, label: '', sortable: false }
];

const _INITIAL_ROW_META_DATA = {
    order: 'asc',
    orderBy: 'statusName',
    page: 0,
    rowsPerPage: 100
};

const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];

const JobReadinessManagement = () => {
    // HOOKS
    const dispatch = useDispatch();
    // LOCAL STATE
    const [editJobReadinessOpen, setEditJobReadinessOpen] = useState(false);
    const [rowMetaData, setRowMetaData] = useState(_INITIAL_ROW_META_DATA);
    const [jobReadinessToEdit, setJobReadinessToEdit] = useState({});
    const [rows, setRows] = useState([]);
    const [isDisabled, setIsDisabled] = useState(true);

    // STORE STATE
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const currentParticipantJobReadiness = useSelector(
        (state) => state.entities.participantService.currentParticipantJobReadiness
    );
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const users = useSelector(selectUsersForNames);

    // USE EFFECTS
    useEffect(() => () => cleanUp(), []);

    useEffect(() => {
        dispatch(loadParticipantJobReadiness(currentParticipant.id));

        if (!currentParticipant?.id) return;
        const isDisabledOnClosedStatus =
            currentParticipant.status === 'Closed' && !hasRole(EDIT_ON_CLOSED_ROLES, roles);
        setIsDisabled(!hasRole(acceptedRoles, roles) || isDisabledOnClosedStatus);
    }, [currentParticipant.id]);

    useEffect(() => {
        if (!currentParticipantJobReadiness?.length) return;
        const userIds = [...new Set(currentParticipantJobReadiness.map((el) => el.createdBy))];
        const missingUserIds = userIds.filter((el) => !users.find((entry) => entry.id === el));
        missingUserIds.length > 0
            ? dispatch(loadUsersMinimalDetails(missingUserIds))
            : configureRows();
    }, [currentParticipantJobReadiness, users]);

    useEffect(() => {
        if (!rows.length) return;
        configureRows();
    }, [rowMetaData]);

    const configureRows = () => {
        const participantJobReadiness = currentParticipantJobReadiness.map((el) => {
            const user = users.find((entry) => entry.id === el.createdBy);
            const name = user ? `${user.firstName} ${user.lastName}` : '';
            return {
                ...el,
                user: name
            };
        });
        setRows(
            stableSort(participantJobReadiness, rowMetaData.orderBy, rowMetaData.order).map(
                (el) => {
                    return (
                        <JobReadinessTableRow
                            key={el.id}
                            jobReadinessEntry={el}
                            onEdit={onEditJobReadiness}
                        />
                    );
                }
            ) || []
        );
    };

    // HELPER FN
    const loadingError = () => {
        if (!currentParticipantJobReadiness?.length) return PARTICIPANT_JOB_READINESS_ERROR;
    };

    const cleanUp = () => dispatch(clearParticipantJobReadiness());

    // EVENT HANDLERS
    const onEditJobReadiness = (jobReadiness) => {
        setJobReadinessToEdit({
            id: jobReadiness.id,
            jobReadinessId: jobReadiness.jobReadinessId,
            jobReadinessReasonId: jobReadiness.jobReadinessReasonId,
            participantId: currentParticipant.id
        });
        setEditJobReadinessOpen(true);
    };

    const onCloseEditJobReadiness = () => {
        setJobReadinessToEdit({});
        setEditJobReadinessOpen(false);
    };

    // RENDER
    const errorMsg = loadingError();

    return (
        <div>
            <h2 className={app.sectionHeading}>Job Readiness</h2>
            {errorMsg ? (
                <LoadingSpinner content={errorMsg} />
            ) : (
                <ResultsTable
                    defaultOrderBy={rowMetaData.orderBy}
                    sortOrder={rowMetaData.order}
                    defaultRowsPerPage={rowMetaData.rowsPerPage}
                    headCells={_HEAD_CELLS}
                    passRowMetaDataUp={setRowMetaData}
                    tableRows={rows}
                    totalResults={currentParticipantJobReadiness.length}
                />
            )}
            {editJobReadinessOpen && currentParticipant?.contractId ? (
                <CreateEditJobReadiness
                    jobReadinessEntry={jobReadinessToEdit}
                    onCancel={onCloseEditJobReadiness}
                />
            ) : (
                <Button
                    id="employabilityAddJobReadinessButton"
                    disabled={isDisabled || !currentParticipant?.contractId}
                    content="Add Job Readiness"
                    onClick={() => setEditJobReadinessOpen(true)}
                />
            )}
        </div>
    );
};

export default JobReadinessManagement;
